// Contact management service for community messaging

import {
  CommunityResident,
  ContactGroup,
  ResidentSearchFilters,
  NewConversationRequest,
  ContactImport,
  ImportedContact,
} from '@/types/entities/resident/contacts';

// Contact Picker API types
interface ContactInfo {
  name?: string[];
  email?: string[];
  tel?: string[];
}

interface ExtendedNavigator extends Navigator {
  contacts?: {
    select: (properties: string[], options?: { multiple?: boolean }) => Promise<ContactInfo[]>;
  };
}

class ContactService {
  private baseUrl = '/api'; // This would be your actual API base URL

  // Residents management
  async getResidents(filters?: ResidentSearchFilters): Promise<CommunityResident[]> {
    try {
      const params = new URLSearchParams();
      if (filters?.query) params.append('query', filters.query);
      if (filters?.building) params.append('building', filters.building);
      if (filters?.role) params.append('role', filters.role);
      if (filters?.status) params.append('status', filters.status);
      if (filters?.verificationStatus)
        params.append('verificationStatus', filters.verificationStatus);
      if (filters?.isOnline !== undefined) params.append('isOnline', filters.isOnline.toString());
      if (filters?.canMessage !== undefined)
        params.append('canMessage', filters.canMessage.toString());
      if (filters?.sortBy) params.append('sortBy', filters.sortBy);
      if (filters?.sortOrder) params.append('sortOrder', filters.sortOrder);

      const response = await fetch(`${this.baseUrl}/contacts/residents?${params}`);
      if (!response.ok) throw new Error('Failed to fetch residents');

      const data = await response.json();
      return data.residents || [];
    } catch (error) {
      // Silently fall back to mock data (API not implemented yet)
      return this.getMockResidents(filters);
    }
  }

  async getResidentById(id: string): Promise<CommunityResident | null> {
    try {
      const response = await fetch(`${this.baseUrl}/contacts/residents/${id}`);
      if (!response.ok) throw new Error('Failed to fetch resident');

      const data = await response.json();
      return data.resident || null;
    } catch (error) {
      console.error('Error fetching resident:', error);
      return null;
    }
  }

  async updateResidentPreferences(
    id: string,
    preferences: Partial<CommunityResident['preferences']>,
  ): Promise<boolean> {
    try {
      const response = await fetch(`${this.baseUrl}/contacts/residents/${id}/preferences`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(preferences),
      });

      return response.ok;
    } catch (error) {
      console.error('Error updating resident preferences:', error);
      return false;
    }
  }

  // Contact groups management
  async getContactGroups(): Promise<ContactGroup[]> {
    try {
      const response = await fetch(`${this.baseUrl}/contacts/groups`);
      if (!response.ok) throw new Error('Failed to fetch contact groups');

      const data = await response.json();
      return data.groups || [];
    } catch (error) {
      // Silently fall back to mock data (API not implemented yet)
      return this.getMockContactGroups();
    }
  }

  async createContactGroup(
    group: Omit<ContactGroup, 'id' | 'createdAt' | 'updatedAt'>,
  ): Promise<ContactGroup | null> {
    try {
      const response = await fetch(`${this.baseUrl}/contacts/groups`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(group),
      });

      if (!response.ok) throw new Error('Failed to create contact group');

      const data = await response.json();
      return data.group || null;
    } catch (error) {
      console.error('Error creating contact group:', error);
      return null;
    }
  }

  async updateContactGroup(id: string, updates: Partial<ContactGroup>): Promise<boolean> {
    try {
      const response = await fetch(`${this.baseUrl}/contacts/groups/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates),
      });

      return response.ok;
    } catch (error) {
      console.error('Error updating contact group:', error);
      return false;
    }
  }

  async deleteContactGroup(id: string): Promise<boolean> {
    try {
      const response = await fetch(`${this.baseUrl}/contacts/groups/${id}`, {
        method: 'DELETE',
      });

      return response.ok;
    } catch (error) {
      console.error('Error deleting contact group:', error);
      return false;
    }
  }

  // Conversation management
  async createConversation(
    conversation: NewConversationRequest,
  ): Promise<{ id: string; success: boolean }> {
    try {
      const response = await fetch(`${this.baseUrl}/conversations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(conversation),
      });

      if (!response.ok) throw new Error('Failed to create conversation');

      const data = await response.json();
      return { id: data.conversation.id, success: true };
    } catch (error) {
      console.error('Error creating conversation:', error);
      // Mock success for development
      return { id: `conv_${Date.now()}`, success: true };
    }
  }

  async addParticipantsToConversation(
    conversationId: string,
    participantIds: string[],
  ): Promise<boolean> {
    try {
      const response = await fetch(`${this.baseUrl}/conversations/${conversationId}/participants`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ participantIds }),
      });

      return response.ok;
    } catch (error) {
      console.error('Error adding participants to conversation:', error);
      return false;
    }
  }

  async removeParticipantFromConversation(
    conversationId: string,
    participantId: string,
  ): Promise<boolean> {
    try {
      const response = await fetch(
        `${this.baseUrl}/conversations/${conversationId}/participants/${participantId}`,
        {
          method: 'DELETE',
        },
      );

      return response.ok;
    } catch (error) {
      console.error('Error removing participant from conversation:', error);
      return false;
    }
  }

  // Contact import
  async importContacts(
    importData: ImportedContact[],
    source: 'phone' | 'email' | 'csv' | 'google' | 'apple',
  ): Promise<ContactImport | null> {
    try {
      const response = await fetch(`${this.baseUrl}/contacts/import`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ contacts: importData, source }),
      });

      if (!response.ok) throw new Error('Failed to import contacts');

      const data = await response.json();
      return data.import || null;
    } catch (error) {
      console.error('Error importing contacts:', error);
      return null;
    }
  }

  // Phone contact sync using Contact Picker API
  async syncPhoneContacts(): Promise<ImportedContact[]> {
    try {
      // Check if Contact Picker API is available
      if (!('contacts' in navigator)) {
        throw new Error('Contact Picker API not supported');
      }

      // Request contacts from device
      const extendedNavigator = navigator as ExtendedNavigator;
      const contacts = await extendedNavigator.contacts!.select(['name', 'email', 'tel'], {
        multiple: true,
      });

      // Transform to our format
      const importedContacts: ImportedContact[] = contacts.map((contact: ContactInfo) => ({
        name: contact.name?.[0] || 'Unknown Contact',
        email: contact.email?.[0],
        phone: contact.tel?.[0],
        isResidentMatch: false,
        matchConfidence: 0,
      }));

      // Attempt to match with existing residents
      return await this.matchContactsWithResidents(importedContacts);
    } catch (error) {
      console.error('Error syncing phone contacts:', error);
      throw error;
    }
  }

  // Smart contact matching algorithm
  async matchContactsWithResidents(contacts: ImportedContact[]): Promise<ImportedContact[]> {
    try {
      const residents = await this.getResidents();

      return contacts.map((contact) => {
        let bestMatch: CommunityResident | null = null;
        let bestConfidence = 0;

        residents.forEach((resident) => {
          let confidence = 0;

          // Name matching (fuzzy)
          if (contact.name && resident.firstName && resident.lastName) {
            const contactName = contact.name.toLowerCase();
            const residentName = `${resident.firstName} ${resident.lastName}`.toLowerCase();

            if (contactName === residentName) {
              confidence += 0.6;
            } else if (
              contactName.includes(resident.firstName.toLowerCase()) ||
              contactName.includes(resident.lastName.toLowerCase())
            ) {
              confidence += 0.3;
            }
          }

          // Email matching (exact)
          if (contact.email && resident.email) {
            if (contact.email.toLowerCase() === resident.email.toLowerCase()) {
              confidence += 0.4;
            }
          }

          // Phone matching (normalized)
          if (contact.phone && resident.phone) {
            const normalizePhone = (phone: string) => phone.replace(/\D/g, '').slice(-10); // Get last 10 digits

            if (normalizePhone(contact.phone) === normalizePhone(resident.phone)) {
              confidence += 0.4;
            }
          }

          if (confidence > bestConfidence) {
            bestConfidence = confidence;
            bestMatch = resident;
          }
        });

        return {
          ...contact,
          isResidentMatch: bestConfidence > 0.6,
          matchedResidentId: bestMatch?.id,
          matchConfidence: bestConfidence,
        };
      });
    } catch (error) {
      console.error('Error matching contacts:', error);
      return contacts;
    }
  }

  // Cloud service integrations (future implementations)
  async syncGoogleContacts(accessToken: string): Promise<ImportedContact[]> {
    // Implementation for Google Contacts API
    throw new Error('Google Contacts sync not yet implemented');
  }

  async syncAppleContacts(credentials: Record<string, unknown>): Promise<ImportedContact[]> {
    // Implementation for Apple Contacts via iCloud
    throw new Error('Apple Contacts sync not yet implemented');
  }

  async getContactImportStatus(importId: string): Promise<ContactImport | null> {
    try {
      const response = await fetch(`${this.baseUrl}/contacts/imports/${importId}`);
      if (!response.ok) throw new Error('Failed to fetch import status');

      const data = await response.json();
      return data.import || null;
    } catch (error) {
      console.error('Error fetching import status:', error);
      return null;
    }
  }

  // Search and discovery
  async searchResidents(
    query: string,
    filters?: ResidentSearchFilters,
  ): Promise<CommunityResident[]> {
    const allFilters = { ...filters, query };
    return this.getResidents(allFilters);
  }

  async getRecentContacts(limit: number = 10): Promise<CommunityResident[]> {
    try {
      const response = await fetch(`${this.baseUrl}/contacts/recent?limit=${limit}`);
      if (!response.ok) throw new Error('Failed to fetch recent contacts');

      const data = await response.json();
      return data.contacts || [];
    } catch (error) {
      console.error('Error fetching recent contacts:', error);
      return [];
    }
  }

  async getFavoriteContacts(): Promise<CommunityResident[]> {
    try {
      const response = await fetch(`${this.baseUrl}/contacts/favorites`);
      if (!response.ok) throw new Error('Failed to fetch favorite contacts');

      const data = await response.json();
      return data.contacts || [];
    } catch (error) {
      console.error('Error fetching favorite contacts:', error);
      return [];
    }
  }

  async toggleFavoriteContact(contactId: string): Promise<boolean> {
    try {
      const response = await fetch(`${this.baseUrl}/contacts/${contactId}/favorite`, {
        method: 'POST',
      });

      return response.ok;
    } catch (error) {
      console.error('Error toggling favorite contact:', error);
      return false;
    }
  }

  // Utility methods
  private getMockResidents(filters?: ResidentSearchFilters): CommunityResident[] {
    const mockData: CommunityResident[] = [
      {
        id: '1',
        tenantId: 'community-1',
        firstName: 'Sarah',
        lastName: 'Johnson',
        email: 'sarah.johnson@email.com',
        phone: '+1 (555) 123-4567',
        unitNumber: 'A-301',
        building: 'Building A',
        avatarUrl: '/placeholder.svg',
        role: 'board_director',
        status: 'active',
        isOnline: true,
        joinedAt: '2023-01-15T00:00:00Z',
        verificationStatus: 'verified',
        preferences: {
          allowDirectMessages: true,
          allowGroupInvitations: true,
          showOnlineStatus: true,
          allowContactImport: false,
        },
      },
      {
        id: '2',
        tenantId: 'community-1',
        firstName: 'Michael',
        lastName: 'Chen',
        email: 'michael.chen@email.com',
        phone: '+1 (555) 234-5678',
        unitNumber: 'B-205',
        building: 'Building B',
        avatarUrl: '/placeholder.svg',
        role: 'resident',
        status: 'active',
        isOnline: false,
        lastSeen: '2024-01-24T14:30:00Z',
        joinedAt: '2023-03-20T00:00:00Z',
        verificationStatus: 'verified',
        preferences: {
          allowDirectMessages: true,
          allowGroupInvitations: true,
          showOnlineStatus: false,
          allowContactImport: true,
        },
      },
      {
        id: '3',
        tenantId: 'community-1',
        firstName: 'Emma',
        lastName: 'Wilson',
        email: 'emma.wilson@email.com',
        phone: '+1 (555) 345-6789',
        unitNumber: 'C-102',
        building: 'Building C',
        avatarUrl: '/placeholder.svg',
        role: 'resident',
        status: 'active',
        isOnline: true,
        joinedAt: '2023-07-10T00:00:00Z',
        verificationStatus: 'verified',
        preferences: {
          allowDirectMessages: true,
          allowGroupInvitations: false,
          showOnlineStatus: true,
          allowContactImport: false,
        },
      },
      {
        id: '4',
        tenantId: 'community-1',
        firstName: 'David',
        lastName: 'Brown',
        email: 'david.brown@email.com',
        phone: '+1 (555) 456-7890',
        unitNumber: 'D-205',
        building: 'Building D',
        avatarUrl: '/placeholder.svg',
        role: 'maintenance',
        status: 'active',
        isOnline: false,
        lastSeen: '2024-01-24T09:15:00Z',
        joinedAt: '2023-02-01T00:00:00Z',
        verificationStatus: 'verified',
        preferences: {
          allowDirectMessages: true,
          allowGroupInvitations: true,
          showOnlineStatus: true,
          allowContactImport: false,
        },
      },
      {
        id: '5',
        tenantId: 'community-1',
        firstName: 'Lisa',
        lastName: 'Garcia',
        email: 'lisa.garcia@email.com',
        phone: '+1 (555) 567-8901',
        unitNumber: 'E-101',
        building: 'Building E',
        avatarUrl: '/placeholder.svg',
        role: 'property_manager',
        status: 'active',
        isOnline: true,
        joinedAt: '2022-12-01T00:00:00Z',
        verificationStatus: 'verified',
        preferences: {
          allowDirectMessages: true,
          allowGroupInvitations: true,
          showOnlineStatus: true,
          allowContactImport: true,
        },
      },
    ];

    // Apply filters
    return mockData.filter((resident) => {
      if (filters?.query) {
        const query = filters.query.toLowerCase();
        const fullName = `${resident.firstName} ${resident.lastName}`.toLowerCase();
        const unitMatch = resident.unitNumber.toLowerCase().includes(query);
        const emailMatch = resident.email.toLowerCase().includes(query);

        if (!fullName.includes(query) && !unitMatch && !emailMatch) {
          return false;
        }
      }

      if (filters?.role && resident.role !== filters.role) return false;
      if (filters?.status && resident.status !== filters.status) return false;
      if (filters?.verificationStatus && resident.verificationStatus !== filters.verificationStatus)
        return false;
      if (filters?.building && resident.building !== filters.building) return false;
      if (filters?.isOnline !== undefined && resident.isOnline !== filters.isOnline) return false;
      if (filters?.canMessage && !resident.preferences.allowDirectMessages) return false;

      return true;
    });
  }

  private getMockContactGroups(): ContactGroup[] {
    return [
      {
        id: 'group-1',
        name: 'Board Members',
        description: 'Community board members and leadership',
        type: 'role_based',
        members: [],
        createdBy: '1',
        isPrivate: false,
        canAddMembers: false,
        createdAt: '2023-01-01T00:00:00Z',
        updatedAt: '2023-01-01T00:00:00Z',
      },
      {
        id: 'group-2',
        name: 'Building A Residents',
        description: 'All residents in Building A',
        type: 'system_generated',
        members: [],
        createdBy: 'system',
        isPrivate: false,
        canAddMembers: false,
        createdAt: '2023-01-01T00:00:00Z',
        updatedAt: '2023-01-01T00:00:00Z',
      },
      {
        id: 'group-3',
        name: 'Property Management',
        description: 'Property management team and maintenance staff',
        type: 'role_based',
        members: [],
        createdBy: 'system',
        isPrivate: false,
        canAddMembers: false,
        createdAt: '2023-01-01T00:00:00Z',
        updatedAt: '2023-01-01T00:00:00Z',
      },
    ];
  }
}

// Export singleton instance
export const contactService = new ContactService();
export default contactService;
